/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Suspense, useState } from 'react';
import type { TablePaginationConfig, TableProps } from 'antd';
import { ISearchSafetyStockReportRes } from '@/interfaces/report';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { twMerge } from 'tailwind-merge';
import DoOrderTableModal from '@/components/doOrder/do-order-table-modal';
import { IDataModal } from '@/interfaces/dataModal';
import InboundItemExternalModal from '@/components/inbound-item/inbound-item-modal';

interface IProps {
  margin: number;
  loading: boolean;
  report: ISearchSafetyStockReportRes[];
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<ISearchSafetyStockReportRes>
      | SorterResult<ISearchSafetyStockReportRes>[],
  ) => void;
}

const _modal: IDataModal<ISearchSafetyStockReportRes> = {
  open: false,
};

const SafetyStockReportTable = ({
  loading,
  report,
  onTableChange,
  margin,
}: IProps) => {
  const [dataModal, setDataModal] = useState<
    IDataModal<ISearchSafetyStockReportRes>
  >({
    ..._modal,
  });

  const [inboundModal, setInboundModal] = useState<
    IDataModal<ISearchSafetyStockReportRes>
  >({
    ..._modal,
  });

  const columns: TableProps<ISearchSafetyStockReportRes>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'center',
      fixed: 'left',
      className: 'text-center',
      render: (_, rec, i) => <div>{i + 1}</div>,
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      className: 'font-medium',
      align: 'center',
      width: 80,
    },
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      key: 'productCode',
      className: 'font-bold',
      align: 'center',
      sorter: (a, b) =>
        (a.productCode || '').localeCompare(b.productCode || ''),
      width: 140,
      render: (_, rec) => (
        <div
          className="underline underline-offset-2 cursor-pointer"
          onClick={() => window.open(`/product/${rec.productId}`, '_blank')}
        >
          {_}
        </div>
      ),
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'productName',
      key: 'productName',
      className: 'font-bold',
      sorter: (a, b) =>
        (a.productName || '').localeCompare(b.productName || ''),
      render: _ => <div className="truncate">{_}</div>,
    },
    {
      title: 'จำนวน',
      dataIndex: 'remainStock',
      key: 'remainStock',
      className: 'font-bold text-blue-700',
      align: 'right',
      sorter: (a, b) => Number(a.remainStock) - Number(b.remainStock),
      render: (_: number) => formatNumber(_, 2),
      width: 120,
    },
    {
      title: 'In Transit',
      dataIndex: 'inboundQty',
      key: 'inboundQty',
      align: 'right',
      width: 120,
      sorter: (a, b) => Number(a.inboundQty - b.inboundQty),
      render: (_: number, rec) => (
        <div
          onClick={() =>
            setInboundModal({
              open: true,
              id: rec.productId,
            })
          }
          className={twMerge(
            _ && 'text-blue-700 underline underline-offset-2 cursor-pointer',
          )}
        >
          {formatNumber(_ || 0, 2)}
        </div>
      ),
    },
    {
      title: 'DO',
      dataIndex: 'doRemainQty',
      key: 'doRemainQty',
      align: 'right',
      width: 120,
      sorter: (a, b) => Number(a.doRemainQty - b.doRemainQty),
      render: (_: number, rec) => (
        <div
          onClick={() =>
            setDataModal({
              open: true,
              id: rec.productId,
            })
          }
          className={twMerge(
            _ && 'text-blue-700 underline underline-offset-2 cursor-pointer',
          )}
        >
          {formatNumber(_ || 0, 2)}
        </div>
      ),
    },
    {
      title: 'หน่วย',
      dataIndex: 'unitName',
      key: 'unitName',
      className: 'font-bold',
      align: 'center',
      width: 80,
    },
    {
      title: 'Safety Stock',
      dataIndex: 'safetyStock',
      key: 'safetyStock',
      align: 'right',
      defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        Number(a.remainStock - a.safetyStock) -
        Number(b.remainStock - b.safetyStock),
      render: (_: number, rec) => (
        <div
          className={twMerge(
            'font-bold text-green-700',
            rec.remainStock < _ - Number((_ * margin) / 100) && 'text-red-600',
          )}
        >
          {formatNumber(_ || 0, 2)}
        </div>
      ),
    },
    {
      title: 'DIff',
      dataIndex: 'diff',
      key: 'diff',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          Number(rec.remainStock || 0) - Number(rec.safetyStock || 0),
          2,
        ),
    },
    {
      title: '%',
      dataIndex: 'diff',
      key: 'diff',
      align: 'right',
      sorter: (a, b) =>
        Number(a.safetyStock && (a.remainStock / a.safetyStock) * 100) -
        Number(b.safetyStock && (b.remainStock / b.safetyStock) * 100),
      render: (_: number, rec) =>
        (rec.safetyStock &&
          formatNumber(
            rec.safetyStock && (rec.remainStock / rec.safetyStock) * 100,
            2,
            2,
          )) ||
        '',
    },
    {
      title: 'รอส่ง',
      dataIndex: 'orderDraftQty',
      key: 'orderDraftQty',
      sorter: (a, b) => Number(a.orderDraftQty) - Number(b.orderDraftQty),
      align: 'right',
      render: (_: number, rec) => (
        <div
          className={twMerge(
            'font-bold text-blue-700',
            rec.remainStock < _ - Number((_ * margin) / 100) && 'text-red-600',
          )}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: '1 month',
      dataIndex: 'orderQty1',
      key: 'orderQty1',
      sorter: (a, b) =>
        Number(a.remainStock - a.orderQty1) -
        Number(b.remainStock - b.orderQty1),
      align: 'right',
      render: (_: number, rec) => (
        <div
          className={twMerge(
            'font-bold text-green-700',
            rec.remainStock < _ - Number((_ * margin) / 100) && 'text-red-600',
          )}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
    {
      title: '3 month',
      dataIndex: 'orderQty2',
      key: 'orderQty2',
      className: 'font-bold text-green-700',
      align: 'right',
      sorter: (a, b) =>
        Number(a.remainStock - a.orderQty2) -
        Number(b.remainStock - b.orderQty2),
      render: (_: number, rec) => (
        <div
          className={twMerge(
            'font-bold text-green-700',
            rec.remainStock < _ - Number((_ * margin) / 100) && 'text-red-600',
          )}
        >
          {formatNumber(_, 2)}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ISearchSafetyStockReportRes>
        columns={columns}
        criteria={undefined}
        data={{ data: report, totalRecords: report.length || 0 }}
        loading={loading}
        onTableChange={onTableChange}
        showPagination={false}
        scrollLength={1600}
      />
      <Suspense>
        {dataModal?.id && (
          <DoOrderTableModal
            open={dataModal?.open}
            productId={Number(dataModal?.id)}
            handleCancel={function (): void {
              setDataModal({
                ..._modal,
              });
            }}
          />
        )}
        {inboundModal?.id && (
          <InboundItemExternalModal
            open={inboundModal?.open}
            productId={Number(inboundModal?.id)}
            handleCancel={function (): void {
              setInboundModal({
                ..._modal,
              });
            }}
            isSafetyView
          />
        )}
      </Suspense>
    </div>
  );
};

export default SafetyStockReportTable;
